<script setup lang="ts">
import { computed } from 'vue'

import { Overlay } from '@collector/shared-ui'
import { getOppositeParticipant } from '@collector/shared-ui-domain'
import {
  ReceptionAttribute,
  VolleyballIncidentId,
} from '@collector/sportsapi-client-legacy'
import {
  useParticipants,
  useTeamSides,
} from '@desktop/views/Relation/composables'
import IncidentButton from '@desktop/views/Relation/Sports/components/RelationView/IncidentButton.vue'
import {
  useBlockades,
  useEventIncidentsQueue,
} from '@desktop/views/Relation/Sports/composables'

import { useServeIncident } from './useServeIncident'

// composables
const { isPitchBlocked } = useBlockades()
const { lastServeParticipantTeamId, lastServeTeamSide, isReceptionVisible } =
  useServeIncident()
const { homeParticipant, awayParticipant } = useParticipants()
const { currentFirstTeamSide } = useTeamSides()
const eventIncidentsQueue = useEventIncidentsQueue()

// methods
function addIncidentToQueue(attributeId: ReceptionAttribute): void {
  const home = homeParticipant.value
  const away = awayParticipant.value
  if (!home || !away) return

  const oppositeParticipant = getOppositeParticipant(
    lastServeParticipantTeamId.value,
    home,
    away,
  )

  eventIncidentsQueue.addIncident({
    incident_id: VolleyballIncidentId.Reception,
    participant_team_id: oppositeParticipant.id,
    attribute_id: [attributeId],
  })
}

// computed
const isVisible = computed(() =>
  isPitchBlocked() ? false : isReceptionVisible.value,
)
</script>

<template>
  <Overlay
    class="border-neutral-light-10 border"
    :modelValue="isVisible"
  >
    <div
      class="bg-neutral-light-10 absolute flex h-full w-1/2 gap-x-0.5"
      :class="{
        'right-0': lastServeTeamSide === currentFirstTeamSide,
        'flex-row-reverse': lastServeTeamSide !== currentFirstTeamSide,
      }"
    >
      <IncidentButton
        class="w-1/2 rounded-none! text-6xl! font-light"
        :incidentId="VolleyballIncidentId.Reception"
        variant="score"
        @click="addIncidentToQueue(ReceptionAttribute.Plus)"
      >
        +
      </IncidentButton>

      <IncidentButton
        class="w-full rounded-none! text-6xl! font-light"
        :incidentId="VolleyballIncidentId.Reception"
        variant="missed"
        @click="addIncidentToQueue(ReceptionAttribute.Negative)"
      >
        -
      </IncidentButton>
    </div>

    <div
      class="absolute flex h-full w-1/2"
      :class="{
        'right-0': lastServeTeamSide !== currentFirstTeamSide,
        'flex-row-reverse': lastServeTeamSide === currentFirstTeamSide,
      }"
    >
      <div
        class="bg-neutral-light-4 border-neutral-light-10 w-1/2"
        :class="{
          'border-r-1 border-l-2': lastServeTeamSide !== currentFirstTeamSide,
          'border-r-2 border-l-1': lastServeTeamSide === currentFirstTeamSide,
        }"
      />
      <div class="bg-neutral-light-4 w-full" />
    </div>
  </Overlay>
</template>
